import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';

import { Table, Column, Cell } from 'fixed-data-table-2';

import s from './VariantTable.css';

const FilteredCell = ({ data, rowIndex, columnKey, hoverRow, ...props }) => {
  let output = data[rowIndex][columnKey];
  let style = {};
  if (hoverRow === rowIndex) {
    style = { backgroundColor: '#f6f6f6' };
  }
  // Limit length of allele output
  if (columnKey === 'ref_allele' || columnKey === 'alt_allele') {
    if (output.length > 9) {
      output = output.substring(0, 9);
      output += '...';
    }
  }
  return (
    <div style={style}>
      <Cell {...props}>{output}</Cell>
    </div>
  );
};

const FeatureCell = ({
  data, // eslint-disable-line
  rowIndex, // eslint-disable-line
  columnKey, // eslint-disable-line
  showFeatures, // eslint-disable-line
  hoverRow, // eslint-disable-line
  ...props
}) => {
  // eslint-disable-next-line react/prop-types
  let output = data[rowIndex].features[showFeatures][columnKey];
  const outFloat = parseFloat(output);
  if (!isNaN(outFloat) && !Number.isInteger(outFloat))
    output = parseFloat(output).toFixed(3);

  if (output === 'TRUE') output = <i className="fa fa-check" />;
  if (output === 'FALSE') output = null;

  let style = {};
  if (hoverRow === rowIndex) {
    style = { backgroundColor: '#f6f6f6' };
  }
  return (
    <div style={style}>
      <Cell {...props}>{output}</Cell>
    </div>
  );
};

FilteredCell.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      rsid: PropTypes.string,
      gene: PropTypes.string,
      position: PropTypes.number,
      chromosome: PropTypes.string,
      alt_allele: PropTypes.string,
      ref_allele: PropTypes.string,
    }),
  ),
  rowIndex: PropTypes.number,
  columnKey: PropTypes.string,
  hoverRow: PropTypes.number,
};

FilteredCell.defaultProps = {
  data: [],
  rowIndex: 0,
  columnKey: '',
  hoverRow: -1,
};

/*
 * General component for searching for and selecting a variant
 */
class VariantTable extends Component {
  static propTypes = {
    variants: PropTypes.arrayOf(PropTypes.object),
    showFeatures: PropTypes.string,
    onSubmit: PropTypes.func,
    featureNames: PropTypes.arrayOf(PropTypes.string),
    containerWidth: PropTypes.number,
  };

  static defaultProps = {
    variants: null,
    onSubmit: null,
    showFeatures: null,
    featureNames: [],
    containerWidth: 800,
  };

  constructor(props) {
    super(props);

    this.rowClicked = this.rowClicked.bind(this);
    this.onRowEnter = this.onRowEnter.bind(this);

    this.state = {
      hoverRow: -1,
    };
  }

  onRowEnter(e, rowIndex) {
    this.setState({ hoverRow: rowIndex });
  }

  // This function is called when the particular row is called, creating the url
  // for the position and gene that it will correspond to
  rowClicked(e, rowIndex) {
    if (this.props.onSubmit)
      this.props.onSubmit(
        this.props.variants[rowIndex].gene_entrez,
        this.props.variants[rowIndex].rsid,
        this.props.variants[rowIndex].position,
      );
  }

  render() {
    const { hoverRow } = this.state;
    const { containerWidth, showFeatures, featureNames, variants } = this.props;

    const rowClassName = () => s.row;
    let columns = [];
    if (!showFeatures) {
      columns.push(
        <Column
          columnKey="rsid"
          key="rsid"
          width={150}
          header={<Cell>RSID</Cell>}
          flexGrow={1}
          cell={<FilteredCell data={variants} hoverRow={hoverRow} />}
        />,
      );
      columns.push(
        <Column
          columnKey="gene"
          key="gene"
          width={100}
          header={<Cell>GENE</Cell>}
          flexGrow={1}
          cell={<FilteredCell data={variants} hoverRow={hoverRow} />}
        />,
      );
      columns.push(
        <Column
          columnKey="ref_allele"
          key="ref_allele"
          width={100}
          header={<Cell>REFRENCE ALLELE</Cell>}
          cell={<FilteredCell data={variants} hoverRow={hoverRow} />}
        />,
      );
      columns.push(
        <Column
          columnKey="alt_allele"
          key="alt_allele"
          width={100}
          header={<Cell>ALTERNATIVE ALLELE</Cell>}
          cell={<FilteredCell data={variants} hoverRow={hoverRow} />}
        />,
      );
    }

    // Add feature columns if shoWFeatures prop is set
    if (showFeatures && featureNames)
      columns = columns.concat(
        featureNames.map(feature => (
          <Column
            columnKey={feature}
            key={feature}
            width={135}
            header={<Cell>{feature}</Cell>}
            cell={
              <FeatureCell
                data={variants}
                showFeatures={showFeatures}
                hoverRow={hoverRow}
              />
            }
          />
        )),
      );

    return (
      <div
        style={{
          textAlign: 'center',
          margin: 'auto',
          width: `${containerWidth}px`,
        }}
      >
        <Table
          height={variants.length * 30 + 80}
          rowsCount={variants.length}
          rowHeight={30}
          width={containerWidth}
          headerHeight={60}
          onRowClick={this.rowClicked}
          rowClassNameGetter={rowClassName}
          onRowMouseEnter={this.onRowEnter}
        >
          <Column
            columnKey="chromosome"
            key="chromosome"
            width={60}
            header={<Cell>CHR</Cell>}
            fixed
            cell={<FilteredCell data={variants} hoverRow={hoverRow} />}
          />
          <Column
            columnKey="position"
            key="position"
            width={140}
            header={<Cell>POSITION</Cell>}
            fixed
            flexGrow={1}
            cell={<FilteredCell data={variants} hoverRow={hoverRow} />}
          />
          {columns}
        </Table>
      </div>
    );
  }
}

export default withStyles(s)(VariantTable);
